import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  signInWithGoogle,
  signUpWithPassword,
  signInWithPassword,
  buildAuthEmailRedirectUrl,
  logAuthErrorDev,
  isInviteAcceptIntentNextPath,
  setAuthRememberMePreference,
} from "@tennis-matches/services/auth";
import { PublicRegisterSimple } from "../components/auth/PublicRegisterSimple.jsx";
import { RegisterSuccessPanel } from "../components/auth/RegisterSuccessPanel.jsx";
import {
  AuthBrandPanel,
  AuthMobileBrand,
  AuthPageLogo,
  AuthPasswordToggle,
  AuthTextField,
  GoogleIcon,
} from "../components/auth/AuthPageVisuals.jsx";
import { tt } from "@tennis-matches/services/i18n";
import { navigateTo } from "@tennis-matches/core/router";
import { useAuthEmailAvailabilityCheck } from "../hooks/useAuthEmailAvailabilityCheck.js";
import { usePasswordLogin } from "../hooks/usePasswordLogin.js";
import { useForgotPasswordRequest } from "../hooks/useForgotPasswordRequest.js";
import { usePublicRegistrationForm } from "../hooks/usePublicRegistrationForm.js";
import { Mail, Lock } from "lucide-react";
import "./LoginPage.css";

function __getSafeNextPath() {
  try {
    const sp = new URLSearchParams(window.location.search || "");
    let next = String(sp.get("next") || "").trim();
    if (!next) return "";
    if (!next.startsWith("/")) {
      try {
        const d = decodeURIComponent(next);
        if (d.startsWith("/")) next = d;
      } catch {
        return "";
      }
    }
    if (!next.startsWith("/") || next.startsWith("//")) return "";
    return next;
  } catch {
    return "";
  }
}

/** Fallback, wenn Login nur `invited_email` hat: Token aus Accept-Invite-Seite (sessionStorage). */
function __getStoredInviteAcceptUrl() {
  try {
    const st = String(
      sessionStorage.getItem("tm_access_invite_token_v1") || "",
    ).trim();
    if (!st) return "";
    return `/accept-invite?token=${encodeURIComponent(st)}`;
  } catch {
    return "";
  }
}

function __getQueryValue(key) {
  try {
    const sp = new URLSearchParams(window.location.search || "");
    return String(sp.get(key) || "").trim();
  } catch {
    return "";
  }
}

function __getInvitedEmailFromUrl() {
  const fromInvited = __getQueryValue("invited_email").toLowerCase();
  if (fromInvited) return fromInvited;
  return __getQueryValue("invite_email").toLowerCase();
}

// Brandneue Registrierungen landen direkt im Hub; Onboarding (Verein, Familie,
// Trainer, Training) erfolgt über die Hero-Karten dort. Backwards-Compat für
// alte Drafts (pending_self_access_request): applyPendingSignupAccessRequestRemote
// im Auth-Callback wandelt diese ggf. noch in access_requests um.
const REGISTRATION_NEXT_ROUTE = "hub";

export function LoginPage({ state }) {
  const t = useMemo(
    () => tt(state?.app?.locale || "de-DE"),
    [state?.app?.locale],
  );
  const nextPath = useMemo(() => __getSafeNextPath(), []);
  const invitedEmail = useMemo(() => __getInvitedEmailFromUrl(), []);

  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const [authMode, setAuthMode] = useState(() => "login");
  const [invitePassword, setInvitePassword] = useState("");

  const onDraftHydrated = useCallback(() => {
    setAuthMode("register");
  }, []);

  const registration = usePublicRegistrationForm({
    t,
    invitedEmail,
    draftEnabled: authMode === "register" && !invitedEmail,
    onRegistered: () => navigateTo(REGISTRATION_NEXT_ROUTE, true),
    onDraftHydrated,
  });

  const onHandoffToRegister = useCallback(
    (email) => {
      registration.setEmail(email);
      setAuthMode("register");
      setInfo(t("auth.login.redirectToRegisterInfo"));
    },
    [registration.setEmail, t],
  );

  const login = usePasswordLogin({
    t,
    nextPath,
    invitedEmail,
    authMode,
    onHandoffToRegister,
  });
  const forgot = useForgotPasswordRequest({ t });

  const regEmailLiveCheck = useAuthEmailAvailabilityCheck(registration.email, {
    enabled: authMode === "register" && !invitedEmail,
  });

  useEffect(() => {
    registration.applyLiveEmailStatus(regEmailLiveCheck.status);
  }, [regEmailLiveCheck.status, registration.applyLiveEmailStatus]);

  useEffect(() => {
    if (registration.isSuccess) setAuthMode("register-success");
  }, [registration.isSuccess]);

  const switchAuthMode = (mode) => {
    if (mode === "login") {
      login.suppressAutoRegister();
      registration.clearDraft();
      registration.leaveSuccess();
    }
    if (mode === "register") login.allowAutoRegister();
    if (mode === "forgot") {
      forgot.prefillEmail(login.email);
      forgot.clearMessages();
    }
    setAuthMode(mode);
    registration.resetConsents();
    registration.clearError();
    setError("");
    setInfo("");
    login.clearMessages();
  };

  const onGoogle = async () => {
    setError("");
    setInfo("");
    login.clearMessages();
    setBusy("google");
    try {
      setAuthRememberMePreference(login.rememberMe);
      const targetNext = (() => {
        if (nextPath && isInviteAcceptIntentNextPath(nextPath)) return nextPath;
        if (nextPath) return nextPath;
        const stored = __getStoredInviteAcceptUrl();
        if (invitedEmail && stored) return stored;
        if (invitedEmail) return "/accept-invite";
        return `/${REGISTRATION_NEXT_ROUTE}`;
      })();
      const { error: e } = await signInWithGoogle({ nextPath: targetNext });
      if (e) {
        logAuthErrorDev("login/google", e, { endpoint: "auth/v1/authorize" });
        setError(String(e.message || e));
      }
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setBusy("");
    }
  };

  const onInvitedUserLogin = async () => {
    if (!invitedEmail) return;
    setError("");
    setInfo("");
    setBusy("invite-login");
    try {
      const email = String(invitedEmail || "")
        .trim()
        .toLowerCase();
      const password = String(invitePassword || "");
      if (!email || !password) {
        setError(t("auth.validation.emailPasswordRequired"));
        return;
      }
      const { error: e } = await signInWithPassword({ email, password });
      if (e) {
        setError(String(e.message || e));
        return;
      }
      if (nextPath) {
        window.location.assign(nextPath);
        return;
      }
      const storedInvite = __getStoredInviteAcceptUrl();
      if (storedInvite) {
        window.location.assign(storedInvite);
        return;
      }
      navigateTo("accept-invite", true);
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setBusy("");
    }
  };

  const onInviteRegister = async () => {
    setError("");
    setInfo("");
    setBusy("register");
    try {
      const email = String(invitedEmail || "")
        .trim()
        .toLowerCase();
      const password = String(invitePassword || "");
      if (!email || !password) {
        setError(t("auth.validation.emailPasswordRequired"));
        return;
      }
      const { data, error: e } = await signUpWithPassword({
        email,
        password,
        options: {
          emailRedirectTo: buildAuthEmailRedirectUrl(
            isInviteAcceptIntentNextPath(nextPath)
              ? nextPath
              : __getStoredInviteAcceptUrl() || "/accept-invite",
          ),
        },
      });
      if (e) {
        setError(String(e.message || e));
        return;
      }

      const hasSession = Boolean(data?.session?.user?.id);
      if (hasSession && nextPath) {
        window.location.assign(nextPath);
        return;
      }
      if (hasSession) {
        const storedInvite = __getStoredInviteAcceptUrl();
        if (storedInvite) window.location.assign(storedInvite);
        else navigateTo("accept-invite", true);
        return;
      }
      setInfo(t("auth.register.verifyEmailHint"));
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setBusy("");
    }
  };

  const inviteBlock = invitedEmail ? (
    <div className="authPage__inviteBlock">
      <h3 className="authPage__inviteTitle">{t("auth.register.title")}</h3>
      <p className="authPage__welcomeSubtitle">
        {t("auth.register.subtitle", { inviteEmail: invitedEmail })}
      </p>
      <AuthTextField
        id="auth-invite-email"
        label={t("auth.login.email")}
        type="email"
        value={invitedEmail}
        disabled
        autoComplete="email"
        leadingIcon={Mail}
      />
      <AuthTextField
        id="auth-invite-password"
        label={t("auth.login.password")}
        type="password"
        value={invitePassword}
        disabled={busy === "invite-login" || busy === "register"}
        placeholder={t("auth.register.passwordPlaceholder")}
        autoComplete="new-password"
        leadingIcon={Lock}
        onChange={(e) => setInvitePassword(String(e.target.value || ""))}
      />
      <div className="authPage__inviteActions">
        <button
          type="button"
          className="authPage__ghostBtn"
          disabled={busy === "invite-login" || busy === "register"}
          onClick={() => void onInvitedUserLogin()}
        >
          {busy === "invite-login"
            ? t("common.loading")
            : t("auth.register.loginWithInviteEmail")}
        </button>
        <button
          type="button"
          className="authPage__submitBtn"
          disabled={busy === "invite-login" || busy === "register"}
          onClick={() => void onInviteRegister()}
        >
          {busy === "register"
            ? t("common.loading")
            : t("auth.register.createAccountAndAccept")}
        </button>
      </div>
    </div>
  ) : null;

  const loginPanel = (
    <>
      <button
        type="button"
        className="authPage__googleBtn"
        disabled={busy === "google"}
        onClick={() => void onGoogle()}
      >
        <GoogleIcon size={20} />
        <span>{t("auth.login.google")}</span>
      </button>
      <div className="authPage__divider" role="separator">
        {t("auth.login.orEmail")}
      </div>
      <div className="authPage__form">
        <AuthTextField
          id="auth-login-email"
          label={t("auth.login.email")}
          type="email"
          value={login.email}
          disabled={login.busy}
          autoComplete="email"
          leadingIcon={Mail}
          onChange={(e) => login.onEmailChange(e.target.value)}
        />
        {login.emailCheck.status === "checking" ? (
          <p className="authPage__emailStatus" role="status">
            {t("auth.register.emailCheck.checking")}
          </p>
        ) : null}
        <AuthTextField
          id="auth-login-password"
          label={t("auth.login.password")}
          type={login.showPassword ? "text" : "password"}
          value={login.password}
          disabled={login.busy}
          autoComplete="current-password"
          leadingIcon={Lock}
          trailing={
            <AuthPasswordToggle
              visible={login.showPassword}
              onToggle={() => login.setShowPassword((v) => !v)}
              labelShow={t("auth.login.showPassword")}
              labelHide={t("auth.login.hidePassword")}
            />
          }
          onChange={(e) => login.setPassword(String(e.target.value || ""))}
        />
        <div className="authPage__forgotRow">
          <button
            type="button"
            className="authPage__textLink"
            onClick={() => switchAuthMode("forgot")}
          >
            {t("auth.login.forgotPassword")}
          </button>
        </div>
        <label className="authPage__rememberRow">
          <input
            type="checkbox"
            className="authPage__rememberCheckbox"
            checked={login.rememberMe}
            disabled={login.busy}
            onChange={(e) => login.setRememberMe(e.target.checked)}
          />
          <span>{t("auth.login.rememberMe")}</span>
        </label>
        <button
          type="button"
          className="authPage__submitBtn"
          disabled={login.busy}
          onClick={() => void login.onEmailPasswordLogin()}
        >
          {login.busy ? t("common.loading") : t("auth.login.submit")}
        </button>
      </div>
      <p className="authPage__footerSwitch">
        {t("auth.page.footerNoAccount")}{" "}
        <button
          type="button"
          className="authPage__footerSwitchBtn"
          onClick={() => switchAuthMode("register")}
        >
          {t("auth.page.footerRegister")}
        </button>
      </p>
    </>
  );

  const registerPanel = (
    <>
      <div className="loginPage__panel loginPage__panel--register">
        <PublicRegisterSimple
          {...registration.formProps}
          onSwitchToLogin={() => {
            const email = String(registration.email || "")
              .trim()
              .toLowerCase();
            if (email) {
              login.setEmail(email);
              login.suppressAutoRegister();
            }
            switchAuthMode("login");
          }}
          emailLiveStatus={regEmailLiveCheck.status}
          emailLiveBackendUnavailable={
            regEmailLiveCheck.backendUnavailable === true
          }
        />
      </div>
      <p className="authPage__footerSwitch">
        {t("auth.page.footerHasAccount")}{" "}
        <button
          type="button"
          className="authPage__footerSwitchBtn"
          onClick={() => switchAuthMode("login")}
        >
          {t("auth.page.footerLogin")}
        </button>
      </p>
    </>
  );

  const forgotPanel = (
    <>
      <p className="authPage__welcomeSubtitle authPage__forgotIntro">
        {t("auth.forgotPassword.subtitle")}
      </p>
      <div className="authPage__form">
        <AuthTextField
          id="auth-forgot-email"
          label={t("auth.login.email")}
          type="email"
          value={forgot.email}
          disabled={forgot.busy}
          autoComplete="email"
          leadingIcon={Mail}
          onChange={(e) => forgot.setEmail(String(e.target.value || ""))}
        />
        <button
          type="button"
          className="authPage__submitBtn"
          disabled={forgot.busy}
          onClick={() => void forgot.onSubmit()}
        >
          {forgot.busy
            ? t("auth.forgotPassword.sending")
            : t("auth.forgotPassword.submit")}
        </button>
      </div>
      <p className="authPage__footerSwitch">
        <button
          type="button"
          className="authPage__footerSwitchBtn"
          onClick={() => switchAuthMode("login")}
        >
          {t("auth.forgotPassword.backToLogin")}
        </button>
      </p>
    </>
  );

  const handleUseDifferentEmail = () => {
    registration.successProps.onUseDifferentEmail();
    setError("");
    setInfo("");
    setAuthMode("register");
  };

  const handleSuccessBackToLogin = () => {
    login.setEmail(registration.successProps.email || "");
    login.suppressAutoRegister();
    registration.leaveSuccess();
    setError("");
    setInfo("");
    login.clearMessages();
    setAuthMode("login");
  };

  const bannerError =
    authMode === "forgot"
      ? forgot.error
      : login.error || registration.error || error;
  const bannerInfo = authMode === "forgot" ? forgot.info : info || login.info;

  const mainAuthBlock = !invitedEmail ? (
    authMode === "forgot" ? (
      forgotPanel
    ) : authMode === "register-success" ? (
      <RegisterSuccessPanel
        t={t}
        email={registration.successProps.email}
        onBackToLogin={handleSuccessBackToLogin}
        onUseDifferentEmail={handleUseDifferentEmail}
        onResendEmail={registration.successProps.onResendEmail}
        resendBusy={registration.successProps.resendBusy}
        resendNotice={registration.successProps.resendNotice}
      />
    ) : (
      <>
        <div
          className="authPage__modeTabs"
          role="tablist"
          aria-label={t("auth.page.modeTabsAria")}
        >
          <button
            type="button"
            role="tab"
            className={`authPage__modeTab${authMode === "login" ? " authPage__modeTab--active" : ""}`}
            aria-selected={authMode === "login"}
            onClick={() => switchAuthMode("login")}
          >
            {t("auth.mode.login")}
          </button>
          <button
            type="button"
            role="tab"
            className={`authPage__modeTab${authMode === "register" ? " authPage__modeTab--active" : ""}`}
            aria-selected={authMode === "register"}
            onClick={() => switchAuthMode("register")}
          >
            {t("auth.mode.register")}
          </button>
        </div>
        {authMode === "login" ? loginPanel : registerPanel}
      </>
    )
  ) : null;

  const welcomeTitle =
    authMode === "forgot"
      ? t("auth.forgotPassword.title")
      : authMode === "register-success" && !invitedEmail
        ? ""
        : authMode === "register" && !invitedEmail
          ? t("auth.page.registerTitle")
          : t("auth.page.welcomeTitle");
  const welcomeSubtitle =
    authMode === "forgot"
      ? ""
      : authMode === "register-success" && !invitedEmail
        ? ""
        : authMode === "register" && !invitedEmail
          ? t("auth.page.registerSubtitle")
          : t("auth.page.welcomeSubtitle");

  return (
    <div className="authPage">
      <div className="authPage__bg" aria-hidden="true" />
      <div className="authPage__inner">
        <header className="authPage__header">
          <AuthPageLogo t={t} />
        </header>

        <AuthMobileBrand t={t} />

        <div className="authPage__card">
          <AuthBrandPanel t={t} />

          <div className="authPage__formCol">
            <div className="authPage__formCard">
              {!invitedEmail && welcomeTitle ? (
                <div className="authPage__welcome">
                  <h2 className="authPage__welcomeTitle">{welcomeTitle}</h2>
                  {welcomeSubtitle ? (
                    <p className="authPage__welcomeSubtitle">
                      {welcomeSubtitle}
                    </p>
                  ) : null}
                </div>
              ) : null}

              {login.loginNotice === "already_registered" ? (
                <p
                  className="authPage__message authPage__message--info authPage__inlineNotice"
                  role="status"
                >
                  <span>{t("auth.login.alreadyRegisteredMessage")} </span>
                  <button
                    type="button"
                    className="authPage__textLink"
                    onClick={() => {
                      login.setLoginNotice("");
                      switchAuthMode("forgot");
                    }}
                  >
                    {t("auth.login.alreadyRegisteredForgot")}
                  </button>
                  <span> {t("auth.login.alreadyRegisteredOr")} </span>
                  <button
                    type="button"
                    className="authPage__footerSwitchBtn"
                    onClick={() => {
                      login.setLoginNotice("");
                      setError("");
                      setInfo("");
                      switchAuthMode("login");
                    }}
                  >
                    {t("auth.login.alreadyRegisteredBack")}
                  </button>
                </p>
              ) : null}
              {bannerError ? (
                <p
                  className="authPage__message authPage__message--error"
                  role="alert"
                >
                  {t("auth.login.error")}: {bannerError}
                </p>
              ) : null}
              {bannerInfo && authMode !== "register-success" ? (
                <p className="authPage__message authPage__message--info">
                  {bannerInfo}
                </p>
              ) : null}

              {!invitedEmail ? (
                mainAuthBlock
              ) : (
                <>
                  <label className="authPage__rememberRow authPage__rememberRow--invite">
                    <input
                      type="checkbox"
                      className="authPage__rememberCheckbox"
                      checked={login.rememberMe}
                      disabled={busy === "google"}
                      onChange={(e) => login.setRememberMe(e.target.checked)}
                    />
                    <span>{t("auth.login.rememberMe")}</span>
                  </label>
                  <button
                    type="button"
                    className="authPage__googleBtn"
                    disabled={busy === "google"}
                    onClick={() => void onGoogle()}
                  >
                    <GoogleIcon size={20} />
                    <span>{t("auth.login.google")}</span>
                  </button>
                  <div className="authPage__divider" role="separator">
                    {t("auth.login.orEmail")}
                  </div>
                  {inviteBlock}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
